// Bearer token auth for Sovereign MUSE.
// Single user — no login flow, no tiers, no Stripe.
// Token is dual-persisted: localStorage (fast sync read) + HttpOnly-style cookie
// (survives mobile PWA storage eviction). On load, localStorage is checked first;
// if the browser cleared it (memory pressure, iOS eviction), the cookie recovers it.

import { useState, useCallback } from 'react';
import type { AuthUser } from '../shared/types';

export type { AuthUser } from '../shared/types';

const TOKEN_KEY = 'sovereign_auth_token';
const COOKIE_NAME = 'muse_auth';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

function setCookie(token: string): void {
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Strict; Secure`;
}

function getCookie(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function clearCookie(): void {
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Strict; Secure`;
}

function readToken(): string | null {
  const ls = localStorage.getItem(TOKEN_KEY);
  if (ls) return ls;
  const cookie = getCookie();
  if (cookie) {
    localStorage.setItem(TOKEN_KEY, cookie);
  }
  return cookie;
}

// Synthetic single-user identity for the sovereign instance.
// In the open-source build, the companion determines who this is.
const SOVEREIGN_USER: AuthUser = {
  id: 'sovereign-user',
  email: 'office@funkatorium.org',
  display_name: 'Falco',
  tier: 'creator',
  trial_ends_at: null,
  google_linked: false,
  discord_linked: false,
  default_model: null,
  default_thinking: false,
};

export function useAuth() {
  const [hasToken, setHasToken] = useState<boolean>(() => !!readToken());
  const [loading] = useState(false);

  const setToken = useCallback((token: string) => {
    if (!/^[\x21-\x7e]+$/.test(token)) {
      return;
    }
    localStorage.setItem(TOKEN_KEY, token);
    setCookie(token);
    setHasToken(true);
  }, []);

  const clearToken = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    clearCookie();
    setHasToken(false);
  }, []);

  const logout = useCallback(() => {
    clearToken();
  }, [clearToken]);

  const refetch = useCallback(async () => {
    // No-op for bearer auth — kept for API compatibility with the commercial hook.
  }, []);

  const user: AuthUser | null = hasToken ? SOVEREIGN_USER : null;

  return {
    user,
    loading,
    logout,
    refetch,
    setToken,
    clearToken,
    hasToken,
    isTrial: false,
    isExpired: false,
    isPastDue: false,
    trialDaysLeft: null as number | null,
  };
}

export function getStoredToken(): string | null {
  return readToken();
}
